import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, MoreVertical, Plus, LockKeyhole } from "lucide-react";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import ChangeRoleModal, { TeamMember } from "./ChangeRoleModal";
import { ToastContainer, useToast } from "../Ui_Components/ToastMessage";
import { BASE_URL } from "../api/url";
import { getNutritionistsListWithAssignRole } from "../../services/team";
import { useAuth } from "../../context/auth/AuthContext";
import PermissionGate from "../Auth/PermissionGate";
import { Button } from "../Ui_Components/Button";

interface ApiNutritionist {
  nutrition_id: number;
  nutritionist_name: string;
  assign_role_name: string;
  nutrist_reporting_manager: string;
  nutrist_joining_date: string | null;
  nutrist_last_date: string | null;
  nutrist_rating: string;
  client_assign_count: number;
  nutrist_status: string;
  nutrist_profile: string | null;
}

const buildProfileUrl = (path: string | null): string | null => {
  if (!path) return null;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const AVATAR_PALETTE = [
  "#C084FC",
  "#60A5FA",
  "#34D399",
  "#F87171",
  "#FBBF24",
  "#A78BFA",
  "#2DD4BF",
  "#FB923C",
  "#818CF8",
  "#4ADE80",
  "#F472B6",
  "#38BDF8",
  "#E879F9",
  "#F59E0B",
  "#10B981",
  "#6366F1",
];

const avatarColor = (seed: string) =>
  AVATAR_PALETTE[
    seed.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) & 0xffff, 0) %
      AVATAR_PALETTE.length
  ];

const initialsFrom = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

const ITEMS_PER_PAGE = 10;

const TeamPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuth();
  const currentUserId = user?.nutrist_id;
  const {
    toasts,
    removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [roleModalMember, setRoleModalMember] = useState<TeamMember | null>(
    null,
  );
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Bump to force a refetch (e.g. after ChangeRoleModal saves).
  const [refreshTick, setRefreshTick] = useState(0);

  /* Pick up a deferred success toast from a redirecting page
     (e.g. AddNutritionistPage after save). The ref guards against
     React 18 StrictMode's double-invoke of effects in dev, which would
     otherwise fire the toast twice. We also strip the navigation state
     so the toast doesn't re-fire on refresh or back-navigation. */
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (toastFiredRef.current) return;
    const state = location.state as {
      showSuccessToast?: boolean;
      toastMessage?: string;
    } | null;
    if (state?.showSuccessToast) {
      toastFiredRef.current = true;
      showSuccess(state.toastMessage || "Success");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchNutritionists = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getNutritionistsListWithAssignRole(
          "",
          "",
          currentPage,
          ITEMS_PER_PAGE,
          searchQuery,
        );
        if (cancelled) return;
        if (data.success === "1") {
          const mapped: TeamMember[] = (data.nutritionists ?? []).map(
            (n: ApiNutritionist, idx: number) => ({
              id: n.nutrition_id ?? idx + 1,
              nutritionId: n.nutrition_id ?? idx + 1,
              name: n.nutritionist_name ?? "",
              designation: n.assign_role_name ?? "",
              reportingManager: n.nutrist_reporting_manager ?? "",
              clientsAssigned: n.client_assign_count ?? 0,
              joiningDate: n.nutrist_joining_date ?? "",
              status: n.nutrist_status === "Active" ? "Active" : "Inactive",
              initials: initialsFrom(n.nutritionist_name ?? ""),
              avatarColor: avatarColor(n.nutritionist_name ?? String(idx)),
              employeeId: "",
              currentPermissions: [],
              profileUrl: buildProfileUrl(n.nutrist_profile),
            }),
          );
          setTeamMembers(mapped);
          setTotalPages(data.total_pages ?? 1);
          setTotalCount(data.total_count ?? mapped.length);
        } else {
          setError(data.message || "Failed to load nutritionists");
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load nutritionists",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNutritionists();
    return () => {
      cancelled = true;
    };
  }, [currentPage, searchQuery, refreshTick]);

  const paged = teamMembers;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const openEditModal = (member: TeamMember) => {
    setOpenMenuId(null);
    setRoleModalMember(member);
  };

  return (
    <div
      className="h-screen flex flex-col bg-[#F7F7FA]"
      onClick={() => setOpenMenuId(null)}
    >
      <PageHeader title="Team" />

      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">
          {/* Card Header */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-gray-900">
              Total nutritionist ({totalCount})
            </h3>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm 3xl:text-[16px] font-inter"
                />
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <PermissionGate resource="admin" action="assign_roles">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => navigate("/team/roles-access")}
                  className="flex items-center gap-1.5 "
                >
                  <LockKeyhole className="w-4 h-4" />
                  Manage roles &amp; access
                </Button>
              </PermissionGate>
              <PermissionGate resource="admin" action="manage_nutritionists">
                <Button
                  variant="primary"
                  onClick={() => navigate("/team/add-nutritionist")}
                  startIcon={<Plus className="w-4 h-4" />}
                  className="whitespace-nowrap"
                >
                  Add
                </Button>
              </PermissionGate>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5">
              <table className="w-full min-w-[700px] table-fixed">
                <thead>
                  <tr className="bg-white">
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[25%]">
                      Team name
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[18%]">
                      Designation
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[18%]">
                      Reporting manager
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[13%]">
                      Client assigned
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[13%]">
                      Joining date
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[10%]">
                      Status
                    </th>
                    <th className="w-[3%]" />
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
              {loading ? (
                <div className="flex items-center justify-center h-full py-16">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-base text-gray-500 font-inter">
                      Loading team
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-red-500 text-sm sm:text-base 3xl:text-[18px] font-inter">
                    {error}
                  </p>
                </div>
              ) : paged.length === 0 ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-gray-400 text-sm sm:text-base 3xl:text-[18px] font-inter">
                    {searchQuery.trim()
                      ? "No team members match your search"
                      : "No team members yet"}
                  </p>
                </div>
              ) : (
                <table className="w-full min-w-[700px] table-fixed">
                  <tbody>
                    {paged.map((member, index) => (
                      <tr
                        key={member.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(`/team/nutritionist/${member.id}`, {
                            state: { member },
                          })
                        }
                      >
                        {/* Name */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 w-[25%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="relative w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0">
                              {member.profileUrl ? (
                                <img
                                  src={member.profileUrl}
                                  alt={member.name}
                                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover"
                                  onError={(e) => {
                                    const t = e.currentTarget;
                                    t.style.display = "none";
                                    const fb =
                                      t.nextElementSibling as HTMLElement | null;
                                    if (fb) fb.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full items-center justify-center text-white text-xs sm:text-sm 3xl:text-[16px] font-semibold absolute inset-0"
                                style={{
                                  backgroundColor: member.avatarColor,
                                  display: member.profileUrl ? "none" : "flex",
                                }}
                              >
                                {member.initials}
                              </div>
                            </div>
                            <span className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px]">
                              {member.name}
                            </span>
                          </div>
                        </td>

                        {/* Designation */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[18%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          {member.designation}
                        </td>

                        {/* Reporting Manager */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[18%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          {member.reportingManager}
                        </td>

                        {/* Clients Assigned */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[13%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          {member.clientsAssigned}
                        </td>

                        {/* Joining Date */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[13%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          {member.joiningDate}
                        </td>

                        {/* Status */}
                        <td
                          className={`py-3 sm:py-4 px-4 sm:px-6 w-[10%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs 3xl:text-[14px] font-semibold font-inter ${
                              member.status === "Active"
                                ? "bg-[#EBF7F4] text-[#0DB5B5]"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {member.status}
                          </span>
                        </td>

                        {/* Three-dot menu */}
                        <td
                          className={`py-3 sm:py-4 pr-4 w-[3%] ${
                            index !== paged.length - 1
                              ? "border-b border-[#111C2D1A]"
                              : ""
                          }`}
                        >
                          <div
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() =>
                                setOpenMenuId((prev) =>
                                  prev === member.id ? null : member.id,
                                )
                              }
                              className="p-1 rounded hover:bg-gray-100 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-gray-500" />
                            </button>
                            {openMenuId === member.id && (
                              <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-40">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/team/nutritionist/${member.id}`,
                                      { state: { member } },
                                    )
                                  }
                                  className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                >
                                  View profile
                                </button>
                                {/* Hide self-destructive actions: the backend rejects
                                    self role/permission changes ("You cannot change
                                    your own role or permissions."), so don't let the
                                    user walk through the whole modal just to fail. */}
                                {currentUserId !== member.id && (
                                  <PermissionGate
                                    resource="admin"
                                    action="assign_roles"
                                  >
                                    <button
                                      onClick={() => openEditModal(member)}
                                      className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                    >
                                      Change role
                                    </button>
                                  </PermissionGate>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            resultCount={paged.length}
          />
        </div>
      </div>

      {/* Change Role Modal */}
      {roleModalMember && (
        <ChangeRoleModal
          member={roleModalMember}
          onClose={() => setRoleModalMember(null)}
          onSaved={() => {
            setRoleModalMember(null);
            // Refetch the team list so the new role/permission chips show up.
            setRefreshTick((t) => t + 1);
            // If the edited member is the signed-in user, their cached
            // permissions are now stale — pull the fresh set from the backend.
            refreshProfile();
            showSuccess("Role & permissions updated");
          }}
          onError={(message) => showError("Failed to update role", message)}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default TeamPage;
